import React, { createContext, useEffect, useState } from 'react';

type GlobalStateContextProps = {
  isCookieEnabled: boolean;
  setCookieEnabled: (value: boolean) => void;
  language: string;
  setLanguage: (value: string) => void;
};

const GlobalStateContext = createContext<GlobalStateContextProps>({
  isCookieEnabled: false,
  setCookieEnabled: () => {},
  language: 'en',
  setLanguage: () => {},
});

interface GlobalStateProviderProps { children?: React.ReactNode }

export const GlobalStateProvider: React.FC<GlobalStateProviderProps> = ({ children }) => {
  const [cookies, setCookies] = useState(false);
  const [language, setLanguageState] = useState('en');
  const supportedLanguages = ['en','es','fr','de','it','pt','nl','pl','ru','zh','ja','ko','ar','th','vi'];

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const STORAGE_KEY = 'audioenhancer_language';
    
    const updateLanguageFromPath = () => {
      // Read from URL path prefix: /, /pt, /es, ...
      const firstSegment = window.location.pathname.split('/').filter(Boolean)[0] || '';
      let detectedLang: string;
      
      if (supportedLanguages.includes(firstSegment)) {
        // Path has language prefix
        detectedLang = firstSegment;
        // Save to localStorage
        try {
          localStorage.setItem(STORAGE_KEY, detectedLang);
        } catch (e) {
          // Ignore localStorage errors
        }
      } else {
        // No language prefix in path - try to use saved language from localStorage
        try {
          const savedLang = localStorage.getItem(STORAGE_KEY);
          detectedLang = savedLang && supportedLanguages.includes(savedLang) ? savedLang : 'en';
        } catch (e) {
          detectedLang = 'en';
        }
      }
      
      setLanguageState((prevLang) => {
        // Only update if different to avoid unnecessary re-renders
        return prevLang !== detectedLang ? detectedLang : prevLang;
      });
    };
    
    // Initial read
    updateLanguageFromPath();
    
    // Store initial pathname
    let lastPathname = window.location.pathname;
    
    // Listen to popstate (back/forward button)
    const handlePopState = () => {
      updateLanguageFromPath();
      lastPathname = window.location.pathname;
    };
    
    window.addEventListener('popstate', handlePopState);
    
    // Check for pathname changes periodically (Gatsby's router doesn't always trigger popstate)
    // This handles client-side navigation
    const interval = setInterval(() => {
      const currentPathname = window.location.pathname;
      if (currentPathname !== lastPathname) {
        lastPathname = currentPathname;
        updateLanguageFromPath();
      }
    }, 150);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', language || 'en');
    }
  }, [language]);

  const setCookieEnabled = (value: boolean) => setCookies(value);
  const setLanguage = (value: string) => {
    setLanguageState(value);
    // Save to localStorage when language is explicitly set
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('audioenhancer_language', value);
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  };

  const value: GlobalStateContextProps = {
    isCookieEnabled: cookies,
    setCookieEnabled,
    language,
    setLanguage,
  };

  return <GlobalStateContext.Provider value={value}>{children}</GlobalStateContext.Provider>;
};

export default GlobalStateContext;


